import type { SourceAttributeValueBigDecimal, SourceAttributeValueString } from '../api/client/fleet-management'

type BaseSA = Omit<Required<SourceAttributeValueBigDecimal>, 'units'>
type UnitFactory<U> = BaseSA & { units: U }

// Coordinate units
export type CoordinateUnit = 'degrees'
export type CoordinateSourceAttribute = UnitFactory<CoordinateUnit>

// Distance units
export const distanceUnits = ['km', 'm', 'mi'] as const
export type DistanceUnit = (typeof distanceUnits)[number]
export type LongDistanceUnit = 'km' | 'mi'
export type DistanceSourceAttribute = UnitFactory<DistanceUnit>

export function isDistanceUnit(unit?: string): unit is DistanceUnit {
  return unit !== undefined && distanceUnits.includes(unit as DistanceUnit)
}

// Weight units
export const weightUnits = ['kg', 'lb'] as const
export type WeightUnit = (typeof weightUnits)[number]
export type WeightSourceAttribute = UnitFactory<WeightUnit>

// Efficiency units
export const efficiencyUnits = ['km/kwh', 'kwh/km', 'mi/kwh', 'kwh/mi'] as const
export type EfficiencyUnit = (typeof efficiencyUnits)[number]
export type EfficiencySourceAttribute = UnitFactory<EfficiencyUnit>

export function isEfficiencyUnit(unit?: string): unit is EfficiencyUnit {
  return unit !== undefined && efficiencyUnits.includes(unit as EfficiencyUnit)
}

// Emissions units
export const emissionsUnits = ['kg of CO2', 'lb of CO2'] as const
export type EmissionsUnit = (typeof emissionsUnits)[number]
export type EmissionsSourceAttribute = UnitFactory<EmissionsUnit>

export function isEmissionsUnit(unit?: string): unit is EmissionsUnit {
  return ['kg of CO2', 'lb of CO2'].includes(unit ?? '')
}

// Energy units
export const energyUnits = ['wh', 'kwh', 'mwh', 'gwh', 'twh', 'pwh'] as const
export type EnergyUnit = (typeof energyUnits)[number]
export type EnergySourceAttribute = UnitFactory<EnergyUnit>

// Power units
export const powerUnits = ['W', 'KW', 'MW', 'GW', 'TW', 'PW'] as const
export type PowerUnit = (typeof powerUnits)[number]
export type PowerSourceAttribute = UnitFactory<PowerUnit>

// Fuel units
export const fuelUnits = ['gal', 'igal', 'L'] as const
export type FuelUnit = (typeof fuelUnits)[number]
export type FuelSourceAttribute = UnitFactory<FuelUnit>

// Percent units
export const percentUnit = 'percent' as const
export type PercentUnit = typeof percentUnit
export type PercentSourceAttribute = UnitFactory<PercentUnit>

// Speed units
export const speedUnits = ['kph', 'mph'] as const
export type SpeedUnit = (typeof speedUnits)[number]
export type SpeedSourceAttribute = UnitFactory<SpeedUnit>

export function isSpeedUnit(unit?: string): unit is SpeedUnit {
  return unit !== undefined && speedUnits.includes(unit as SpeedUnit)
}

// Temperature units
export const temperatureUnits = ['celsius', 'fahrenheit'] as const
export type TemperatureUnit = (typeof temperatureUnits)[number]
export type TemperatureSourceAttribute = UnitFactory<TemperatureUnit>

export function isTemperatureUnit(unit?: string): unit is TemperatureUnit {
  return unit !== undefined && temperatureUnits.includes(unit as TemperatureUnit)
}

// Time units
export type TimeSourceAttribute = UnitFactory<TimeUnits>
export enum TimeUnits {
  MILLISECONDS = 'milliseconds',
  SECONDS = 's',
  MINUTES = 'min',
  HOURS = 'hr',
  DAYS = 'd'
}

// SourceAttribute is the union of all source attribute types
export type SourceAttribute =
  | CoordinateSourceAttribute
  | DistanceSourceAttribute
  | EfficiencySourceAttribute
  | EnergySourceAttribute
  | EmissionsSourceAttribute
  | TimeSourceAttribute
  | PercentSourceAttribute

export type SourceAttributeValue = SourceAttributeValueString | SourceAttributeValueBigDecimal
export const isSourceAttributeValue = (value: unknown): value is SourceAttributeValue => {
  if (!value || typeof value !== 'object') return false
  const sourceAttributeKeys = ['source', 'units', 'value']
  const valueKeys = Object.keys(value)
  return sourceAttributeKeys.every((key) => valueKeys.includes(key))
}
